/**
 * InlineBanner — reusable page-level status banner.
 *
 * Appears inside the page layout (not floating), with room for a title and an
 * optional checklist. Matches the visual language of the existing "match result"
 * and "share result" banners in JobDetail.
 *
 * Usage:
 *   <InlineBanner
 *     variant="success"
 *     title="Campaign accepted"
 *     items={[
 *       'Added to your library (4 tracks)',
 *       'Pushed to your FTP (4 files)',
 *       'Campaign added to Campaigns',
 *     ]}
 *     autoDismissMs={10000}
 *     onClose={() => setShowBanner(false)}
 *   />
 */

import React, { useEffect } from 'react';

export type BannerVariant = 'success' | 'warning' | 'error' | 'info';

interface Props {
  variant: BannerVariant;
  title?: string;
  message?: string;
  items?: string[];
  onClose?: () => void;
  autoDismissMs?: number;
  style?: React.CSSProperties;
}

const styles: Record<BannerVariant, { bg: string; border: string; text: string; icon: string }> = {
  success: {
    bg: 'rgba(80, 200, 120, 0.1)',
    border: 'rgba(80, 200, 120, 0.3)',
    text: '#50c878',
    icon: 'tim-icons icon-check-2',
  },
  warning: {
    bg: 'rgba(255, 180, 50, 0.1)',
    border: 'rgba(255, 180, 50, 0.3)',
    text: '#ffb432',
    icon: 'tim-icons icon-alert-circle-exc',
  },
  error: {
    bg: 'rgba(255, 80, 80, 0.1)',
    border: 'rgba(255, 80, 80, 0.3)',
    text: '#ff5050',
    icon: 'tim-icons icon-simple-remove',
  },
  info: {
    bg: 'rgba(90, 170, 255, 0.1)',
    border: 'rgba(90, 170, 255, 0.3)',
    text: '#5aaaff',
    icon: 'tim-icons icon-bell-55',
  },
};

const InlineBanner: React.FC<Props> = ({ variant, title, message, items, onClose, autoDismissMs, style }) => {
  useEffect(() => {
    if (!autoDismissMs || !onClose) return;
    const t = setTimeout(onClose, autoDismissMs);
    return () => clearTimeout(t);
  }, [autoDismissMs, onClose]);

  const s = styles[variant];

  return (
    <div
      style={{
        marginBottom: '1rem',
        padding: '0.75rem 1rem',
        backgroundColor: s.bg,
        borderRadius: '6px',
        border: `1px solid ${s.border}`,
        color: s.text,
        fontSize: '0.9rem',
        ...style,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{ flex: 1 }}>
          {title && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: items?.length || message ? 6 : 0 }}>
              <i className={s.icon} />
              <span>{title}</span>
            </div>
          )}
          {message && (
            <div style={{ color: 'rgba(255,255,255,0.8)' }}>{message}</div>
          )}
          {items && items.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: title ? 24 : 0, listStyle: 'none' }}>
              {items.map((item, i) => (
                <li key={i} style={{ color: 'rgba(255,255,255,0.85)', padding: '2px 0' }}>
                  <i className="tim-icons icon-check-2" style={{ fontSize: '0.7rem', color: s.text, marginRight: 6 }} />
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: 'inherit',
              cursor: 'pointer', fontSize: '1.1rem', padding: 0, lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Close"
          >&times;</button>
        )}
      </div>
    </div>
  );
};

export default InlineBanner;
